import { VariantProps, cva } from 'class-variance-authority'
import { Info } from 'lucide-react'

import { paths } from '@/config/paths'
import { LinkButton } from '@/ui/atoms/button/Button'
import { cn } from '@/ui/utils/style'

const bannerVariants = cva('flex rounded-lg border border-blue-500/20 bg-blue-500/5', {
  variants: {
    variant: {
      full: 'flex-col gap-4 p-4 md:p-6',
      compact: 'flex-col items-start gap-3 p-3 sm:flex-row sm:items-center md:p-4',
    },
  },
  defaultVariants: {
    variant: 'full',
  },
})

export interface LegacyMarketBannerProps extends VariantProps<typeof bannerVariants> {
  className?: string
  showExploreLink?: boolean
}

/**
 * Informational banner for deprecated Legacy Isolated Markets.
 *
 * Two variants:
 * - `full`: For the markets list page with complete messaging and prominent CTAs
 * - `compact`: For individual market pages with condensed messaging
 */
export function LegacyMarketBanner({ variant = 'full', className, showExploreLink = true }: LegacyMarketBannerProps) {
  if (variant === 'compact') {
    return <CompactBanner className={className} showExploreLink={showExploreLink} />
  }

  return <FullBanner className={className} showExploreLink={showExploreLink} />
}

function FullBanner({ className, showExploreLink }: { className?: string; showExploreLink: boolean }) {
  return (
    <div className={cn(bannerVariants({ variant: 'full' }), className)}>
      <div className="flex items-start gap-3">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-blue-400" />
        <div className="flex flex-col gap-2">
          <h3 className="font-semibold text-foreground">Legacy Isolated Markets — Deprecated</h3>
          <p className="text-muted-foreground text-sm leading-relaxed">
            These isolated markets are deprecated. No deposited funds are at risk — markets remain open for lending and
            withdrawing assets. No additional new borrows are available. As lenders move capital to other markets,
            borrow rates are expected to increase to encourage repays. We encourage you to move your assets to
            HypurrFi&apos;s active markets for enhanced borrowing capability.
          </p>
        </div>
      </div>

      {showExploreLink && (
        <div className="flex flex-wrap gap-2 pl-8 sm:gap-3">
          <LinkButton href={paths.earn} variant="outline" size="sm">
            Explore Markets →
          </LinkButton>
        </div>
      )}
    </div>
  )
}

function CompactBanner({ className, showExploreLink }: { className?: string; showExploreLink: boolean }) {
  return (
    <div className={cn(bannerVariants({ variant: 'compact' }), className)}>
      <div className="flex flex-1 items-start gap-3">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />
        <p className="text-muted-foreground text-sm leading-relaxed">
          <span className="font-medium text-foreground">This is a Legacy isolated market.</span> No deposited funds are
          at risk — you can continue to lend and withdraw. New borrows are no longer available.
        </p>
      </div>

      {showExploreLink && (
        <div className="flex shrink-0 items-center gap-2 pl-7 text-sm sm:pl-0">
          <span className="hidden text-muted-foreground sm:inline">Move to active markets →</span>
          <div className="flex gap-2">
            <LinkButton href={paths.earn} variant="outline" size="sm" className="h-7 px-2 text-xs">
              Explore Markets →
            </LinkButton>
          </div>
        </div>
      )}
    </div>
  )
}
